/**
 * StatCard Component
 *
 * Unified component for statistics display in toolbars.
 */

import type { ReactNode } from 'react';
import clsx from 'clsx';

export interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: number | string;
  color?: 'blue' | 'violet' | 'rose' | 'emerald' | 'amber';
  subValue?: string;
  size?: 'sm' | 'md';
}

export function StatCard({
  icon,
  label,
  value,
  color = 'violet',
  subValue,
  size = 'md',
}: StatCardProps) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-accent-blue/10 text-accent-blue',
    violet: 'bg-accent-violet/10 text-accent-violet',
    rose: 'bg-accent-rose/10 text-accent-rose',
    emerald: 'bg-accent-emerald/10 text-accent-emerald',
    amber: 'bg-accent-amber/10 text-accent-amber',
  };

  return (
    <div
      className={clsx(
        'rounded-xl bg-surface-primary border border-border-primary',
        size === 'sm' ? 'p-2.5' : 'p-3'
      )}
    >
      <div
        className={clsx(
          'rounded-lg flex items-center justify-center mb-2',
          size === 'sm' ? 'w-7 h-7' : 'w-8 h-8',
          colorClasses[color]
        )}
      >
        {icon}
      </div>
      <div
        className={clsx(
          'font-bold text-text-primary tabular-nums',
          size === 'sm' ? 'text-[16px]' : 'text-[18px]'
        )}
      >
        {value}
      </div>
      {subValue && <p className="text-[10px] text-text-quaternary">{subValue}</p>}
      <div className="text-[10px] text-text-quaternary uppercase tracking-wider font-medium mt-0.5">
        {label}
      </div>
    </div>
  );
}
